<template>
  <avs-card>
    <template v-slot:title>
      <v-card-title class="pl-0">{{ $t("Demande de Depense") }}</v-card-title>
    </template>
    <template v-slot:content>
      <v-container>
        <v-form
          :readonly="!isEditable"
          ref="form"
        >
          <v-select
            v-model="spending.expense_type"
            :items="discountTypes"
            item-text="text"
            item-value="value"
            :rules="rules.expense_type"
            :label="$t('Type de depense')"
            dense
            outlined
            @change="handleExpenseTypeChange"
          />

          <!-- Catégorie de salaire (uniquement pour Salaire) -->
          <v-select
            v-if="spending.expense_type === 'Salary'"
            v-model="spending.salary_category"
            :items="salaryCategories"
            item-text="text"
            item-value="value"
            :rules="rules.salary_category"
            :label="$t('Catégorie')"
            dense
            outlined
          />

          <!-- Mode de paiement -->
          <v-select
            v-model="spending.payment_method"
            :items="paymentMethods"
            item-text="text"
            item-value="value"
            :rules="rules.payment_method"
            :label="$t('Mode de paiement')"
            dense
            outlined
          />

          <!-- Réservation (uniquement pour Event) -->
          <v-autocomplete
            v-if="spending.expense_type === 'Event'"
            v-model="spending.reservation"
            :items="spaces"
            :filter="filterSpaces"
            :rules="rules.reservation"
            :search-input.sync="spaceSearch"
            :label="$t('Reservation')"
            item-value="id"
            :return-object="false"
            dense
            outlined
            clearable
          >
            <template #item="{ item }">
              <div>
                {{ item.customer_info.full_name }} —
                {{ item.title }}
              </div>
            </template>
            <template #selection="{ item }">
              {{ item.customer_info.full_name }}
            </template>
          </v-autocomplete>

          <v-text-field
            v-model="spending.name"
            :disabled="!isEditable"
            :rules="rules.name"
            :label="$t('Titre')"
            counter="64"
            maxlength="64"
            outlined
            dense
          ></v-text-field>

          <v-text-field
            v-model="spending.requested_amount"
            :disabled="!isEditable"
            :rules="rules.requested_amount"
            :label="$t('Montant')"
            counter="64"
            maxlength="64"
            outlined
            dense
          ></v-text-field>

          <v-menu
            v-model="expenseDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            :disabled="!isEditable"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="spending.expense_date"
                :label="$t('Date de dépense')"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                :rules="rules.expense_date"
                :disabled="!isEditable"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="spending.expense_date"
              :max="todayISO"
              @input="expenseDateMenu = false"
            />
          </v-menu>

          <v-textarea
            :disabled="!isEditable"
            rows="5"
            outlined
            v-model="spending.justification"
            :hint="$t('views.template.hint.description')"
            :label="$t('commons.message')"
            className="content-element"
          ></v-textarea>

          <!-- Pièce jointe -->
          <div>
            <v-file-input
              v-model="attachmentFile"
              :disabled="!isEditable"
              :rules="rules.attachment"
              :label="attachmentLabel"
              prepend-icon="mdi-paperclip"
              outlined
              dense
              show-size
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              :hint="$t('Formats acceptés: images, PDF, Word, Excel')"
              persistent-hint
              @change="handleFileChange"
            >
              <template v-slot:selection="{ text }">
                <v-chip
                  small
                  label
                  color="primary"
                >
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>

            <!-- Afficher la pièce jointe existante -->
            <v-alert
              v-if="!isEditable && spending.attachment_url"
              type="info"
              dense
              text
              class="mt-2"
            >
              <div class="d-flex align-center justify-space-between">
                <span>{{ $t("Pièce jointe existante") }}</span>
                <v-btn
                  small
                  color="primary"
                  :href="spending.attachment_url"
                  target="_blank"
                >
                  <v-icon
                    left
                    small
                    >mdi-download</v-icon
                  >
                  {{ $t("Télécharger") }}
                </v-btn>
              </div>
            </v-alert>
          </div>

          <v-col class="d-flex justify-end gap-4">
            <avs-saveButton
              v-if="!loading"
              @click="save"
              :disabled="!isEditable"
            />
            <avs-button
              v-else
              icon
              color="secondary"
              class="spin-loading"
              :iconI="loading ? 'mdi-loading spin' : ''"
              label
              fab
              small
            />
            <avs-cancelButton @click="cancel" />
          </v-col>
        </v-form>
      </v-container>
    </template>
  </avs-card>
</template>

<script>
import { mkSpendingRequest } from "@/utils/datamodels"
import { EXPENSE_TYPES, SALARY_CATEGORIES, PAYMENT_METHODS } from "@/utils/expenseTypes"
import { AuthorisationDetailService, ReservationService } from "@/services"
import { required, max64, isNumber } from "@/utils/validator"
import _ from "lodash"

export default {
  name: "AuthorisationEditor",
  props: {
    hasUniqueName: {
      type: Function,
      required: true
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    value: {
      type: Object
    }
  },
  computed: {
    spending: {
      get() {
        return _.cloneDeep(this.value || this.getDefaultValue())
      }
    },
    project() {
      return this.$store.state.app.project
    },
    todayISO() {
      return new Date().toISOString().slice(0, 10)
    },
    attachmentLabel() {
      const base = this.$t("Pièce jointe")
      if (this.spending.expense_type === "Event") return base + " *"
      return base + " (" + this.$t("optionnel") + ")"
    }
  },
  async created() {
    await this.fetchSpaceTypes()
  },
  data() {
    return {
      defaultItems: [],
      spaces: [],
      spaceSearch: "",
      attachmentFile: null,
      expenseDateMenu: false,
      discountTypes: EXPENSE_TYPES.map(t => ({ text: this.$t(t.label), value: t.value })),
      salaryCategories: SALARY_CATEGORIES.map(c => ({ text: this.$t(c.label), value: c.value })),
      paymentMethods: PAYMENT_METHODS.map(p => ({ text: this.$t(p.label), value: p.value })),
      spendingRequest: mkSpendingRequest(),
      loading: false,
      rules: {
        description: [required],
        name: [required],
        requested_amount: [required, max64, isNumber],
        expense_type: [required],
        reservation: [
          value => {
            if (this.spending.expense_type === "Event" && !value) {
              return this.$t("Ce champ est requis pour un évènement.")
            }
            return true
          }
        ],
        salary_category: [
          value => {
            if (this.spending.expense_type === "Salary" && !value) {
              return this.$t("Ce champ est requis pour un salaire.")
            }
            return true
          }
        ],
        payment_method: [required],
        attachment: [],
        expense_date: [required]
      }
    }
  },
  methods: {
    getDefaultValue() {
      return this.spendingRequest
    },
    cancel() {
      this.$emit("cancel")
    },
    handleExpenseTypeChange() {
      // Réinitialiser les champs conditionnels
      if (this.spending.expense_type === "Event") {
        this.attachmentFile = null
        this.spending.attachment = null
      } else if (this.spending.expense_type === "Current") {
        this.spending.reservation = null
      }
      if (this.spending.expense_type !== "Salary") {
        this.spending.salary_category = null
      }
      this.$refs.form.validate()
    },
    handleFileChange(file) {
      if (file) {
        this.spending.attachment = file
      }
    },
    async fetchSpaceTypes() {
      const response = await ReservationService.get(this, this.project.id)
      this.spaces = response.results || response.data?.results || response.data || []
    },
    filterSpaces(item, query) {
      return `${item.customer_info.full_name} ${item.title}`
        .toLowerCase()
        .includes((query || "").toLowerCase())
    },
    async performSave(data) {
      // Créer un FormData pour gérer les fichiers
      const formData = new FormData()

      // Ajouter tous les champs sauf le fichier
      Object.keys(data).forEach(key => {
        if (
          key !== "attachment" &&
          key !== "attachment_url" &&
          data[key] !== null &&
          data[key] !== undefined
        ) {
          formData.append(key, data[key])
        }
      })

      // Ajouter le fichier s'il existe
      if (this.attachmentFile) {
        formData.append("attachment", this.attachmentFile)
      }

      if (data.id > 0) {
        return AuthorisationDetailService.put(data.id, formData)
      } else {
        return AuthorisationDetailService.post(this.project.id, formData)
      }
    },
    async save() {
      this.loading = true
      this.spending.lang = this.$i18n.locale

      // Ne pas soumettre de catégorie de salaire hors salaire (le filtre FormData ignore null)
      if (this.spending.expense_type !== "Salary" || !this.spending.salary_category) {
        this.spending.salary_category = null
      }

      if (this.$refs.form.validate()) {
        try {
          const response = await this.performSave(this.spending)
          this.$emit("input", response)
          this.$emit("success", this.$t("La demande a été sauvegardé avec succès."))
        } catch (error) {
          const message =
            error?.response?.data?.detail ||
            error?.response?.data?.attachment?.[0] ||
            this.$t("Une erreur est survenue.")
          this.$emit("error", message)
        } finally {
          this.loading = false
        }
      } else {
        this.loading = false
      }
    }
  }
}
</script>
